import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Search,
  Ban,
  CheckCircle,
  Trash2,
  UserCog,
  Eye,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Plus,
  Briefcase,
  KeyRound,
  Database,
  Download,
  Upload,
  Settings,
  Activity,
  Car
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import EmploymentTypesPanel from './EmploymentTypesPanel';
import ActivityPanel from './ActivityPanel';

import { BASE_URL as API } from '../App';

export default function AdminDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('employment'); // 'employment', 'users', 'financial', 'backup', 'footer'
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Footer settings
  const [footerContent, setFooterContent] = useState('');
  const [footerEnabled, setFooterEnabled] = useState(true);
  const [loadingFooter, setLoadingFooter] = useState(false);
  
  // Mileage rate settings
  const [mileageRate, setMileageRate] = useState(0.70);
  const [mileageDescription, setMileageDescription] = useState('IRS Standard Mileage Rate');
  const [loadingMileage, setLoadingMileage] = useState(false);
  
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFinancials, setUserFinancials] = useState(null);
  const [showFinancialDialog, setShowFinancialDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showClearDataDialog, setShowClearDataDialog] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showFinancialSummary, setShowFinancialSummary] = useState(false);
  const [showEmploymentTypeDialog, setShowEmploymentTypeDialog] = useState(false);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    role: 'owner',
    employment_type: ''
  });
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    showPassword: false
  });

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchEmploymentTypes();
  }, []);

  useEffect(() => {
    if (activeTab === 'footer') {
      fetchFooterSettings();
    }
    if (activeTab === 'mileage') {
      fetchMileageSettings();
    }
  }, [activeTab]);

  useEffect(() => {
    // Organize users hierarchically: super_admin at top, then owners with their managers indented underneath
    const organizeUsers = (userList) => {
      const superAdmins = userList.filter(u => u.role === 'super_admin');
      const owners = userList.filter(u => u.role === 'owner');
      const managers = userList.filter(u => u.role === 'manager');
      
      const organized = [];
      
      // Add super admins at the top
      superAdmins.forEach(admin => {
        organized.push(admin);
      });
      
      // Add owners with their managers
      owners.forEach(owner => {
        organized.push(owner);
        // Add managers that belong to this owner
        const ownerManagers = managers.filter(m => 
          m.owner_email === owner.email
        );
        ownerManagers.forEach(manager => {
          organized.push({ ...manager, isIndented: true });
        });
      });
      
      // Add any orphaned managers (without an owner)
      const orphanedManagers = managers.filter(m => !m.owner_email);
      orphanedManagers.forEach(manager => {
        organized.push({ ...manager, isIndented: false });
      });
      
      return organized;
    };

    if (searchTerm) {
      const filtered = users.filter(u => 
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(organizeUsers(filtered));
    } else {
      setFilteredUsers(organizeUsers(users));
    }
  }, [searchTerm, users]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchEmploymentTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/employment-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmploymentTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch employment types');
    }
  };

  const fetchUserFinancials = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/users/${userId}/financial-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserFinancials(response.data);
      setShowFinancialDialog(true);
    } catch (error) {
      toast.error('Failed to fetch user financials');
    }
  };

  const handleSuspendUser = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/users/${userId}`, {
        account_status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User and all data deleted successfully');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleImpersonate = async (userId, userEmail) => {
    try {
      const token = localStorage.getItem('token');
      
      // Store original token for later restoration
      localStorage.setItem('originalToken', token);
      localStorage.setItem('isImpersonating', 'true');
      localStorage.setItem('originalAdminEmail', user.email);
      
      const response = await axios.post(`${API}/api/admin/impersonate/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store new token and user
      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      toast.success(`Now impersonating ${userEmail}`);
      
      // Reload to switch to user's dashboard
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      toast.error('Failed to impersonate user');
    }
  };

  const handleAssignEmploymentType = async (userId, employmentType) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/users/${userId}/employment-type`, 
        { employment_type: employmentType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Employment type assigned successfully');
      fetchUsers();
      setShowEmploymentTypeDialog(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to assign employment type');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUserData.email || !newUserData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      // Super Admin can only create Owner accounts
      const userData = {
        email: newUserData.email,
        password: newUserData.password,
        role: 'owner', // Always create as owner
        permissions: null
      };
      
      await axios.post(`${API}/api/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If employment type is selected, assign it
      if (newUserData.employment_type) {
        const usersResponse = await axios.get(`${API}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const createdUser = usersResponse.data.find(u => u.email === newUserData.email);
        if (createdUser) {
          await axios.put(`${API}/api/admin/users/${createdUser.id}/employment-type`, 
            { employment_type: newUserData.employment_type },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }
      
      toast.success('Owner account created successfully! The owner can now add their own managers.');
      setShowAddUserDialog(false);
      setNewUserData({ email: '', password: '', role: 'owner', employment_type: '' });
      fetchUsers();
      fetchStats(); // Refresh stats to update user count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetPasswordData.newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (resetPasswordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/auth/reset-password`,
        {
          email: selectedUser.email,
          old_password: '',  // Super admin doesn't need old password
          new_password: resetPasswordData.newPassword,
          is_admin_reset: true
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Password reset successfully for ${selectedUser.email}`);
      setResetPasswordData({ newPassword: '', showPassword: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openResetPasswordDialog = (userObj) => {
    setSelectedUser(userObj);
    setResetPasswordData({ newPassword: '', showPassword: true });
    setShowResetPasswordDialog(true);
  };

  const handleClearAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/admin/clear-all-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Cleared ${response.data.income_deleted} income and ${response.data.expenses_deleted} expense entries`);
      setShowClearDataDialog(false);
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  const fetchFooterSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/footer`);
      setFooterContent(response.data.content || '');
      setFooterEnabled(response.data.enabled !== false);
    } catch (error) {
      toast.error('Failed to fetch footer settings');
    }
  };

  const handleUpdateFooter = async () => {
    setLoadingFooter(true);
    try {
      await axios.put(
        `${API}/api/settings/footer`,
        null,
        {
          params: { content: footerContent, enabled: footerEnabled },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      toast.success('Footer updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update footer');
    } finally {
      setLoadingFooter(false);
    }
  };

  const fetchMileageSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/mileage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMileageRate(response.data.rate || 0.70);
      setMileageDescription(response.data.description || 'IRS Standard Mileage Rate');
    } catch (error) {
      console.log('Using default mileage rate');
    }
  };

  const handleUpdateMileage = async () => {
    setLoadingMileage(true);
    try {
      await axios.put(
        `${API}/api/settings/mileage`,
        null,
        {
          params: { rate: mileageRate, description: mileageDescription },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      toast.success('Mileage rate updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mileage rate');
    } finally {
      setLoadingMileage(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-600 mt-2">Manage users and view system-wide analytics</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => window.location.href = '/dashboard/income'}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Income
          </Button>
          <Button 
            onClick={() => window.location.href = '/dashboard/expenses'}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>


      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('employment')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'employment'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Employment Types
          </div>
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'users'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            User Management
          </div>
        </button>
        <button
          onClick={() => setActiveTab('financial')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'financial'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Financial Summary
          </div>
        </button>
        <button
          onClick={() => setActiveTab('backup')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'backup'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Backup & Restore
          </div>
        </button>
        <button
          onClick={() => setActiveTab('mileage')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'mileage'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            Mileage Rate
          </div>
        </button>
        <button
          onClick={() => setActiveTab('footer')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'footer'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Footer Settings
          </div>
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'activity'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Activity Log
          </div>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'employment' && <EmploymentTypesPanel />}

      {activeTab === 'users' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>User Management</CardTitle>
                <CardDescription>View and manage all user accounts</CardDescription>
              </div>
              {stats && (
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                  <div className="text-right">
                    <div className="text-xl font-bold text-slate-900">{stats.total_users}</div>
                    <div className="text-xs text-slate-500">
                      {stats.active_users} active • {stats.suspended_users} suspended
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                onClick={() => setShowAddUserDialog(true)}
                className="whitespace-nowrap"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Owner
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowClearDataDialog(true)}
                className="whitespace-nowrap"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Clear All Data
              </Button>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-3">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No users found</p>
              ) : (
                filteredUsers.map((u) => (
                  <Card key={u.id} className={`border ${u.isIndented ? 'ml-6 border-l-4 border-l-blue-200' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            {u.isIndented && (
                              <div className="text-slate-400">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            )}
                            {u.role !== 'super_admin' ? (
                              <button
                                onClick={() => handleImpersonate(u.id, u.email)}
                                className="font-medium text-slate-900 hover:text-blue-600 hover:underline cursor-pointer text-left"
                                title="Click to impersonate this user"
                              >
                                {u.email}
                              </button>
                            ) : (
                              <div className="font-medium text-slate-900">{u.email}</div>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {format(new Date(u.created_at), 'MMM dd, yyyy')}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Badge variant={u.role === 'owner' ? 'default' : 'secondary'}>
                            {u.role}
                          </Badge>
                          <Badge variant={u.account_status === 'active' ? 'success' : 'destructive'}>
                            {u.account_status}
                          </Badge>
                        </div>
                      </div>

                      {u.employment_type && (
                        <div className="mb-2">
                          <Badge variant="outline" className="gap-1">
                            <Briefcase className="w-3 h-3" />
                            {u.employment_type}
                          </Badge>
                        </div>
                      )}

                      {u.last_login && (
                        <div className="text-xs text-slate-500 mb-3">
                          Last login: {format(new Date(u.last_login), 'MMM dd, yyyy HH:mm')}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchUserFinancials(u.id)}
                          className="w-full"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(u);
                            setShowEmploymentTypeDialog(true);
                          }}
                          className="w-full text-blue-600"
                        >
                          <Briefcase className="w-4 h-4 mr-1" />
                          {u.employment_type ? 'Change' : 'Assign'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSuspendUser(u.id, u.account_status)}
                          className={`w-full ${u.account_status === 'suspended' ? 'text-green-600' : 'text-orange-600'}`}
                        >
                          {u.account_status === 'suspended' ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Activate
                            </>
                          ) : (
                            <>
                              <Ban className="w-4 h-4 mr-1" />
                              Suspend
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setUserToDelete(u);
                            setShowDeleteDialog(true);
                          }}
                          className="w-full text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Role</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Employment Type</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Last Login</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-slate-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <tr key={u.id} className={`border-b hover:bg-slate-50 ${u.isIndented ? 'bg-slate-50/50' : ''}`}>
                        <td className={`py-3 ${u.isIndented ? 'pl-12' : 'px-4'} pr-4`}>
                          <div className="flex items-center gap-2">
                            {u.isIndented && (
                              <div className="text-slate-400">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            )}
                            {u.role !== 'super_admin' ? (
                              <button
                                onClick={() => handleImpersonate(u.id, u.email)}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                                title="Click to impersonate this user"
                              >
                                {u.email}
                              </button>
                            ) : (
                              <div className="font-medium">{u.email}</div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={u.role === 'owner' ? 'default' : 'secondary'}>
                            {u.role}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {u.employment_type ? (
                            <Badge variant="outline" className="gap-1">
                              <Briefcase className="w-3 h-3" />
                              {u.employment_type}
                            </Badge>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedUser(u);
                                setShowEmploymentTypeDialog(true);
                              }}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Assign Type
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={u.account_status === 'active' ? 'success' : 'destructive'}>
                            {u.account_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {u.last_login ? format(new Date(u.last_login), 'MMM dd, HH:mm') : 'Never'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchUserFinancials(u.id)}
                              title="View Financials"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSuspendUser(u.id, u.account_status)}
                              title={u.account_status === 'suspended' ? 'Activate' : 'Suspend'}
                              className={u.account_status === 'suspended' ? 'text-green-600' : 'text-orange-600'}
                            >
                              {u.account_status === 'suspended' ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : (
                                <Ban className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(u);
                                setShowEmploymentTypeDialog(true);
                              }}
                              title="Assign Employment Type"
                              className="text-blue-600"
                            >
                              <Briefcase className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openResetPasswordDialog(u)}
                              title="Reset Password"
                              className="text-purple-600"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setUserToDelete(u);
                                setShowDeleteDialog(true);
                              }}
                              title="Delete User"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Summary Tab */}
      {activeTab === 'financial' && (
        <Card>
          <CardHeader>
            <CardTitle>Financial Summary</CardTitle>
            <CardDescription>System-wide financial overview</CardDescription>
          </CardHeader>
          {stats && (
            <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Income</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-8 h-8 text-emerald-600" />
                    <div>
                      <div className="text-2xl font-bold text-emerald-700">
                        {formatCurrency(stats.total_income)}
                      </div>
                      <div className="text-xs text-slate-500">Across all users</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Expenses</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <TrendingDown className="w-8 h-8 text-red-600" />
                    <div>
                      <div className="text-2xl font-bold text-red-700">
                        {formatCurrency(stats.total_expenses)}
                      </div>
                      <div className="text-xs text-slate-500">Across all users</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">System Profit</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-8 h-8 text-blue-600" />
                    <div>
                      <div className={`text-2xl font-bold ${stats.total_profit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                        {formatCurrency(stats.total_profit)}
                      </div>
                      <div className="text-xs text-slate-500">
                        Income - Expenses
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {/* Backup & Restore Tab */}
      {activeTab === 'backup' && (
        <Card>
          <CardHeader>
            <CardTitle>Backup & Restore</CardTitle>
            <CardDescription>Export and backup all system data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Database Backup Section */}
              <div className="border rounded-lg p-6 bg-slate-50">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Database className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Full Database Backup</h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Export all data including users, financial records, employment types, income/expense categories, and system settings.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Button
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/api/backup/full`, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                              responseType: 'blob'
                            });
                            
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `RevTracker_Backup_${new Date().toISOString().split('T')[0]}.json`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            
                            toast.success('Full backup downloaded successfully');
                          } catch (error) {
                            console.error('Backup error:', error);
                            toast.error(`Failed to download backup: ${error.response?.data?.detail || error.message}`);
                          }
                        }}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download Full Backup (JSON)
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Restore from Backup Section */}
              <div className="border rounded-lg p-6 bg-amber-50 border-amber-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Upload className="w-8 h-8 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2 text-amber-900">Restore from Backup</h3>
                    <p className="text-sm text-amber-700 mb-4">
                      ⚠️ WARNING: This will replace ALL current data with the backup data. This action cannot be undone!
                    </p>
                    <input
                      type="file"
                      accept=".json"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        if (!confirm('⚠️ WARNING: This will DELETE all current data and replace it with the backup. Are you absolutely sure?')) {
                          e.target.value = '';
                          return;
                        }
                        
                        try {
                          const text = await file.text();
                          const backupData = JSON.parse(text);
                          
                          toast.info('Restoring backup... This may take a minute.');
                          
                          const response = await axios.post(`${API}/api/restore`, backupData, {
                            headers: { 
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'application/json'
                            }
                          });
                          
                          toast.success('Backup restored successfully! Please refresh the page.');
                        } catch (error) {
                          console.error('Restore error:', error);
                          toast.error(`Failed to restore backup: ${error.response?.data?.detail || error.message}`);
                        }
                        
                        e.target.value = '';
                      }}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-amber-600 file:text-white hover:file:bg-amber-700"
                    />
                  </div>
                </div>
              </div>

              {/* Info Section */}
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700">
                    <p className="font-medium mb-1">Backup Information</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-600">
                      <li>Backups are exported as JSON files containing all database records</li>
                      <li>Full backup includes all collections and system data</li>
                      <li>Individual backups allow selective data export</li>
                      <li>Store backups securely in a safe location</li>
                      <li>Passwords are exported as hashed values (bcrypt)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mileage Rate Settings Tab */}
      {activeTab === 'mileage' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" />
              Mileage Rate Settings
            </CardTitle>
            <CardDescription>Configure the IRS standard mileage rate for expense tracking</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                The IRS standard mileage rate for 2026 is <strong>$0.70 per mile</strong> for business use.
                This rate is used when tracking mileage on mobile devices.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mileage-rate" className="text-sm font-medium">
                  Mileage Rate ($ per mile)
                </Label>
                <Input
                  id="mileage-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="10"
                  value={mileageRate}
                  onChange={(e) => setMileageRate(parseFloat(e.target.value) || 0)}
                  className="max-w-[200px]"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mileage-description" className="text-sm font-medium">
                  Description
                </Label>
                <Input
                  id="mileage-description"
                  value={mileageDescription}
                  onChange={(e) => setMileageDescription(e.target.value)}
                  placeholder="e.g., IRS Standard Mileage Rate"
                  className="max-w-[400px]"
                />
              </div>
              
              <Button 
                onClick={handleUpdateMileage}
                disabled={loadingMileage}
                className="w-full sm:w-auto"
              >
                {loadingMileage ? 'Updating...' : 'Update Mileage Rate'}
              </Button>
            </div>
            
            {/* Example Calculation */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-medium mb-3">Example Calculation</h3>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Distance</div>
                    <div className="font-semibold">10 miles</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Rate</div>
                    <div className="font-semibold">${mileageRate}/mile</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Total</div>
                    <div className="font-semibold text-green-600">${(10 * mileageRate).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer Settings Tab */}
      {activeTab === 'footer' && (
        <Card>
          <CardHeader>
            <CardTitle>Footer Settings</CardTitle>
            <CardDescription>Manage the website footer content and visibility</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="footer-enabled"
                  checked={footerEnabled}
                  onChange={(e) => setFooterEnabled(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="footer-enabled" className="text-sm font-medium">
                  Enable Footer
                </Label>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="footer-content" className="text-sm font-medium">
                  Footer Content
                </Label>
                <textarea
                  id="footer-content"
                  value={footerContent}
                  onChange={(e) => setFooterContent(e.target.value)}
                  placeholder="Enter footer content (supports HTML)"
                  className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500">
                  You can use HTML tags for formatting (e.g., &lt;br&gt;, &lt;strong&gt;, &lt;a&gt;)
                </p>
              </div>
              
              <Button 
                onClick={handleUpdateFooter}
                disabled={loadingFooter}
                className="w-full sm:w-auto"
              >
                {loadingFooter ? 'Updating...' : 'Update Footer'}
              </Button>
            </div>
            
            {/* Preview Section */}
            {footerContent && (
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium mb-3">Preview</h3>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div 
                    className="text-sm text-gray-600"
                    dangerouslySetInnerHTML={{ __html: footerContent }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && <ActivityPanel />}

      {/* Financial Details Dialog */}
      <Dialog open={showFinancialDialog} onOpenChange={setShowFinancialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>User Financial Summary</DialogTitle>
            <DialogDescription>
              {userFinancials?.email}
            </DialogDescription>
          </DialogHeader>
          {userFinancials && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <div className="text-xs text-slate-600 mb-1">Total Income</div>
                  <div className="text-lg font-bold text-emerald-700">
                    {formatCurrency(userFinancials.total_income)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {userFinancials.income_count} entries
                  </div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <div className="text-xs text-slate-600 mb-1">Total Expenses</div>
                  <div className="text-lg font-bold text-red-700">
                    {formatCurrency(userFinancials.total_expenses)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {userFinancials.expense_count} entries
                  </div>
                </div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="text-sm text-slate-600 mb-1">Net Profit</div>
                <div className={`text-2xl font-bold ${userFinancials.profit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  {formatCurrency(userFinancials.profit)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User Account
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the user account and all associated data.
            </DialogDescription>
          </DialogHeader>
          {userToDelete && (
            <div className="my-4">
              <p className="text-sm font-medium mb-2">You are about to delete:</p>
              <div className="p-3 bg-slate-100 rounded">
                <p className="font-semibold">{userToDelete.email}</p>
                <p className="text-xs text-slate-600">
                  Role: {userToDelete.role} • Created: {format(new Date(userToDelete.created_at), 'MMM dd, yyyy')}
                </p>
              </div>
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-800">
                  <strong>This will delete:</strong>
                </p>
                <ul className="text-xs text-red-700 mt-1 ml-4 list-disc">
                  <li>User account</li>
                  <li>All income entries</li>
                  <li>All expense entries</li>
                  <li>All managers (if owner)</li>
                  <li>All uploaded files</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Data Confirmation Dialog */}
      <Dialog open={showClearDataDialog} onOpenChange={setShowClearDataDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Clear All Financial Data
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete ALL financial data for ALL users.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4">
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
              <p className="text-sm font-bold text-red-900 mb-2">
                ⚠️ WARNING: This is a destructive operation!
              </p>
              <p className="text-sm text-red-800 mb-3">
                This will delete:
              </p>
              <ul className="text-sm text-red-700 ml-4 list-disc space-y-1">
                <li>All income entries for ALL users</li>
                <li>All expense entries for ALL users</li>
                <li>Including SuperAdmin&apos;s financial data</li>
              </ul>
              <p className="text-sm text-red-900 font-semibold mt-3">
                User accounts will NOT be deleted, only financial data.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDataDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleClearAllData}>
              Yes, Clear All Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employment Type Assignment Dialog */}
      <Dialog open={showEmploymentTypeDialog} onOpenChange={setShowEmploymentTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Employment Type</DialogTitle>
            <DialogDescription>
              Select an employment type for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="employment-type">Employment Type</Label>
              <Select
                onValueChange={(value) => handleAssignEmploymentType(selectedUser?.id, value)}
              >
                <SelectTrigger id="employment-type">
                  <SelectValue placeholder="Select employment type" />
                </SelectTrigger>
                <SelectContent>
                  {employmentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEmploymentTypeDialog(false);
              setSelectedUser(null);
            }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Owner Account</DialogTitle>
            <DialogDescription>
              Create a new owner account. Owners can then add their own managers.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-email">Email</Label>
              <Input
                id="new-email"
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                placeholder="user@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                placeholder="Min 6 characters"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-employment-type">Employment Type (Optional)</Label>
              <Select
                value={newUserData.employment_type}
                onValueChange={(value) => setNewUserData({ ...newUserData, employment_type: value })}
              >
                <SelectTrigger id="new-employment-type">
                  <SelectValue placeholder="Select employment type" />
                </SelectTrigger>
                <SelectContent>
                  {employmentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> You are creating an Owner account. The owner will be able to add their own managers after logging in.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddUserDialog(false);
                setNewUserData({ email: '', password: '', role: 'owner', employment_type: '' });
              }}>
                Cancel
              </Button>
              <Button type="submit">Create Owner Account</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.email}</DialogTitle>
            <DialogDescription>
              Set a new password for testing purposes. The new password will be visible.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-password">New Password</Label>
              <div className="relative">
                <Input
                  id="reset-password"
                  type={resetPasswordData.showPassword ? 'text' : 'password'}
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setResetPasswordData({ ...resetPasswordData, showPassword: !resetPasswordData.showPassword })}
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
              {resetPasswordData.showPassword && resetPasswordData.newPassword && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm font-medium text-yellow-900">
                    Password: <span className="font-mono font-bold">{resetPasswordData.newPassword}</span>
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    ⚠️ Save this password - it will be set for {selectedUser?.email}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowResetPasswordDialog(false);
                setResetPasswordData({ newPassword: '', showPassword: false });
                setSelectedUser(null);
              }}>
                Cancel
              </Button>
              <Button type="submit">Reset Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
